/**
 * **What kind of session an occurrence is, and how that is said in one word** — shared by the
 * booking calendar (its panel and its day list) and the client card's „Nadchodzące", so the two
 * cannot call the same session two different things.
 *
 * The wire's `type` names a consultation; anything else — a workout, or a type this build was
 * never taught — is a training. A training the plan put there carries the plan's session name, and
 * that is what makes it „Trening z planu"; the bare kind is „Trening" or „Konsultacja".
 *
 * Pure: no React, no DOM, no query hook, no i18next — keys out, words are the caller's.
 */

export type OccurrenceKind = 'training' | 'consultation';

/** The wire's `type` for a consultation. */
const CONSULTATION_TYPE = 'Consultation';

export function occurrenceKindOf(type: string | null | undefined): OccurrenceKind {
  return type === CONSULTATION_TYPE ? 'consultation' : 'training';
}

/** `Trening` or `Konsultacja` — the kind in one word. */
export function occurrenceKindWordKey(kind: OccurrenceKind): string {
  return kind === 'consultation' ? 'calendar.consultTitle' : 'calendar.adhocTitle';
}

/**
 * The kind as a detail names it: „Trening z planu" for a training the plan named, else the word.
 * `planSessionName` is the plan's own name for the session, trimmed, or `null`.
 */
export function occurrenceKindKey(occurrence: {
  readonly kind: OccurrenceKind;
  readonly planSessionName: string | null;
}): string {
  if (occurrence.kind === 'training' && occurrence.planSessionName !== null) {
    return 'web:booking.kind.fromPlan';
  }
  return occurrenceKindWordKey(occurrence.kind);
}
